'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';

import { useEcomStore, useUiStoreSelector } from '@/data';
import { EcomState } from '@/types';
import { buildAgeGateUrl } from 'app/_components/AgeGate/utils';

import { useStoreAgeGate } from './useStoreAgeGate';

type PendingSwitch = Partial<EcomState> & { next_store_id: string };

/**
 * Routes a store switch that needs a per-location gate through `/age-gate`, leaving `pending` set
 * so `StoreValidator` commits on return. Returns `true` when it redirected — the caller must bail.
 *
 * `returnUrl` defaults to the current path; checkout passes `/` because `StoreValidator` lives in
 * the `(Shop)` layout.
 */
export const useStoreGateRedirect = () => {
  const router = useRouter();
  const needsStoreGate = useStoreAgeGate();
  const [, setStoredState] = useEcomStore();
  const { setState: setUiState } = useUiStoreSelector([]);

  return useCallback(
    (storeId: string, pending: PendingSwitch, returnUrl?: string): boolean => {
      if (!needsStoreGate(storeId)) return false;

      setStoredState(pending);
      setUiState({ is_store_location_open: false });
      router.push(
        buildAgeGateUrl(
          storeId,
          returnUrl ?? window.location.pathname + window.location.search,
        ),
      );
      return true;
    },
    [router, needsStoreGate, setStoredState],
  );
};
